import { styled } from "@linaria/react"
import { Popover, PopoverAnchor, PopoverArrow, PopoverContent, PopoverPortal } from "@radix-ui/react-popover"
import { trim } from "lodash-es"
import { observer } from "mobx-react-lite"
import { useCallback, useRef } from "react"


import { Avatar } from "@/components/Avatar/Avatar.tsx"
import { DropdownMenuStyles } from "@/components/DropdownMenu/DropdownMenuStyles.ts"
import { Ellipsis } from "@/components/Ellipsis/Ellipsis.ts"
import { Vertical } from "@/components/Flex.ts"
import { HighlightMatches } from "@/components/HighlightMatches/HighlightMatches.tsx"
import { getFullName } from "@/hooks/useProfile.ts"
import { FilePlainRegular, Icon20, MentionRegular } from "@/icons/Icons.ts"
import { ellipsis } from "@/styles/mixins.ts"

import type {
  CompletionDropdownItem,
  CompletionModel,
  FileMentionDropdownItem,
  FixedMentionDropdownItem,
  PartialMentionDropdownItem,
  ProfileMentionDropdownItem
} from "@/components/PromptInput/Completion/CompletionModel.ts"


export const CompletionDropdown = observer((props: {
  model: CompletionModel
}) => {
  const selectionIndex = props.model.selectionIndex.get()
  const realItems = props.model.items.get()
  const lastVisibleItems = useRef<CompletionDropdownItem[] | null>(null)
  const isVisible = props.model.isVisible.get()
  if (isVisible) {
    lastVisibleItems.current = realItems
  }
  const items = isVisible ? realItems : (lastVisibleItems.current ?? [])

  return (
    <Popover open={isVisible}>
      <StyledPopoverAnchor />
      <PopoverPortal>
        <StyledPopoverContent
          className={DropdownMenuStyles.Content}
          align="start"
          side="top"
          sideOffset={8}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onPointerDownOutside={() => props.model.close()}
          onEscapeKeyDown={(e) => {
            e.preventDefault()
            props.model.close()
          }}
        >
          <Vertical gap="4px">
            {items.map((item, index) => {
              return (
                <CompletionItem
                  key={item.completedText}
                  item={item}
                  selected={index === selectionIndex}
                  model={props.model}
                />
              )
            })}
          </Vertical>
          <PopoverArrow fill="var(--separator-color)" />
        </StyledPopoverContent>
      </PopoverPortal>
    </Popover>
  )
})

const CompletionItem = observer((props: {
  item: CompletionDropdownItem
  selected: boolean
  model: CompletionModel
}) => {
  const onClick = useCallback(() => {
    props.model.complete(props.item)
  }, [props.item, props.model])
  const query = props.model.query.get()
  return (
    <CompletionItemContainer key={props.item.completedText} selected={props.selected} onClick={onClick}>
      <CompletionItemContent item={props.item} query={query} />
    </CompletionItemContainer>
  )
})

const CompletionItemContent = (props: {
  item: CompletionDropdownItem
  query: string
}) => {
  if (props.item.type === "file") {
    return <FileCompletionItemContent item={props.item} query={props.query} />
  } else if (props.item.type === "profile") {
    return <ProfileCompletionItemContent item={props.item} query={props.query} />
  } else if (props.item.type === "partial") {
    return <PartialCompletionItemContent item={props.item} query={props.query} />
  } else if (props.item.type === "fixed") {
    return <FixedCompletionItemContent item={props.item} query={props.query} />
  }
}

const fileRegex = /^@file:/

const FileCompletionItemContent = (props: {
  item: FileMentionDropdownItem
  query: string
}) => {
  const path = props.item.path.substring(props.item.path.lastIndexOf("/") + 1)
  const filename = path.length === 0 ? "/" : path

  const displayText = props.item.completedText.replace(fileRegex, "")

  return (
    <>
      <FileIcon>
        <FilePlainRegular className={Icon20} />
      </FileIcon>
      <CompletionDescription>
        <PathWithFilename>
          <CompletionValue>
            <HighlightMatches text={filename} query={props.query} />
          </CompletionValue>
          <SecondaryEllipsis>
            <HighlightMatches text={displayText} query={props.query} />
          </SecondaryEllipsis>
        </PathWithFilename>
      </CompletionDescription>
    </>
  )
}

const PathWithFilename = styled(Vertical)`
  gap: 4px
`

const ProfileCompletionItemContent = (props: {
  item: ProfileMentionDropdownItem
  query: string
}) => {
  return (
    <>
      <Avatar profile={props.item.profile} size={24} />
      <CompletionDescription>
        <CompletionValue>
          <HighlightMatches text={getFullName(props.item.profile)} query={props.query} />
        </CompletionValue>
        <SecondaryEllipsis>
          <HighlightMatches text={props.item.profile.username} query={props.query} />
        </SecondaryEllipsis>
      </CompletionDescription>
    </>
  )
}

const mentionTriggerRegex = /^@/

const PartialCompletionItemContent = (props: {
  item: PartialMentionDropdownItem
  query: string
}) => {
  const displayText = trim(props.item.completedText.replace(mentionTriggerRegex, ""), ":")

  return (
    <>
      <FileIcon>
        <FilePlainRegular className={Icon20} />
      </FileIcon>
      <CompletionDescription>
        <CompletionValue>
          <HighlightMatches text={displayText} query={props.query} />
        </CompletionValue>
        <SecondaryEllipsis>
          {props.item.description}
        </SecondaryEllipsis>
      </CompletionDescription>
    </>
  )
}

const FixedCompletionItemContent = (props: {
  item: FixedMentionDropdownItem<string>
  query: string
}) => {
  const displayText = props.item.completedText.replace(mentionTriggerRegex, "")

  return (
    <>
      <FileIcon>
        <MentionRegular className={Icon20} />
      </FileIcon>
      <CompletionDescription>
        <CompletionValue>
          <HighlightMatches text={displayText} query={props.query} />
        </CompletionValue>
        <SecondaryEllipsis>
          {props.item.description}
        </SecondaryEllipsis>
      </CompletionDescription>
    </>
  )
}

const StyledPopoverContent = styled(PopoverContent)`
  width: 480px;
  z-index: var(--z-index-dropdown);
  background-color: var(--text-color-2-opaque);
  border-radius: var(--border-radius-20);
  margin-left: -20px;
  padding: 4px;
`

const SecondaryEllipsis = styled(Ellipsis)`
  color: var(--secondary-text-color);
`

const StyledPopoverAnchor = styled(PopoverAnchor)`
  position: absolute;
  top: 0;
  left: 24px;

  width: 0;
  height: 0;
`

const CompletionItemContainer = styled.div<{
  selected: boolean
}>`
  display: inline-flex;
  flex-flow: row nowrap;
  align-items: center;

  min-width: 0;
  padding: 8px;

  gap: 8px;

  border-radius: var(--border-radius-16);

  white-space: nowrap;

  cursor: pointer;

  background-color: ${props => props.selected ? "var(--text-color-5)" : "transparent"};

  &:hover {
    background-color: ${props => props.selected ? "var(--text-color-10)" : "var(--text-color-5)"};
  }

  &:active {
    background-color: var(--text-color-10);
  }
`

const FileIcon = styled.div`
  width: 20px;
  height: 20px;

  flex: 0 0 auto;
`

const CompletionValue = styled.span`
  font-weight: 500;

  &::after {
    content: " ";
  }
`

const CompletionDescription = styled.div`
  ${ellipsis};
  min-width: 0;
  flex: 1 1 auto;
`